import { useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { detectLanguageFromPath, translatePath, localizedPath } from './pathHelpers';
import { Language, RouteKey, RouteParams } from './routeConfig';

export function useLanguageSync() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isNavigatingRef = useRef(false);

  // On URL change: detect language and sync i18n
  useEffect(() => {
    if (isNavigatingRef.current) {
      isNavigatingRef.current = false;
      return;
    }

    const detected = detectLanguageFromPath(location.pathname);
    if (detected && detected !== i18n.language) {
      i18n.changeLanguage(detected);
    }
  }, [location.pathname, i18n]);

  // On language change: translate URL
  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      const newPath = translatePath(
        location.pathname + location.search,
        lng as Language
      );
      const currentFull = location.pathname + location.search;
      if (newPath !== currentFull) {
        isNavigatingRef.current = true;
        navigate(newPath, { replace: true });
      }
    };

    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n, location.pathname, location.search, navigate]);
}

export function useLocalizedPath() {
  const { i18n } = useTranslation();

  return useCallback(
    (routeKey: RouteKey, params?: RouteParams) =>
      localizedPath(routeKey, params, i18n.language as Language),
    [i18n.language]
  );
}
